import React, {useEffect, useMemo, useState} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {getApplicationById, updateApplication} from '../../../api/HiringAPI'
import {getStorageUrl} from '../../helpers/file'

const statusOptions = [
  'SUBMITTED','UNDER_REVIEW','SCREENING_IN_PROGRESS','SCREENING_PASSED','SCREENING_FAILED','ASSESSMENT_PENDING','ASSESSMENT_IN_PROGRESS','ASSESSMENT_PASSED','ASSESSMENT_FAILED','INTERVIEW_SCHEDULED','INTERVIEW_COMPLETED','SELECTED','REJECTED','WITHDRAWN','ON_HOLD'
]

const ApplicationDetail: React.FC = () => {
  const {id} = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)
  const [status, setStatus] = useState<string>('SUBMITTED')
  const [notes, setNotes] = useState<string>('')
  const [error, setError] = useState<string>('')

  useEffect(() => {
    let mounted = true
    ;(async () => {
      setLoading(true)
      const res = await getApplicationById(String(id))
      if (!mounted) return
      const app = res?.data || res
      setData(app)
      setStatus(app?.status || 'SUBMITTED')
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [id])

  const resumeHref = useMemo(() => {
    const url = data?.candidate?.resumeUrl
    if (!url) return ''
    // Ensure absolute URL to bypass SPA routing and auth redirects
    if (url.startsWith('http')) return url
    return getStorageUrl(url)
  }, [data])

  const save = async () => {
    if (!id) return
    setSaving(true)
    setError('')
    const res = await updateApplication(String(id), { status, internalNotes: notes })
    if (!(res?.success === false)) {
      navigate(-1)
    } else {
      setError(res?.message || 'Failed to update application')
    }
    setSaving(false)
  }

  if (loading || !data) return <div className='card'><div className='card-body'>Loading...</div></div>

  return (
    <div className='card'>
      <div className='card-header d-flex align-items-center'>
        <h3 className='card-title mb-0 fw-bold'>Application — {data.candidate?.firstName} {data.candidate?.lastName}</h3>
        <div className='ms-auto'>
          <button className='btn btn-light me-3' onClick={() => navigate(-1)}>Back</button>
          <button className='btn btn-primary' disabled={saving} onClick={save}>Save</button>
        </div>
      </div>
      <div className='card-body p-5'>
        {error && <div className='alert alert-danger'>{error}</div>}
        <div className='row g-10'>
          <div className='col-lg-6'>
            <div className='mb-6'>
              <div className='fw-bold fs-4 mb-2'>Candidate</div>
              <div className='text-gray-700'>{data.candidate?.firstName} {data.candidate?.lastName}</div>
              <div className='text-gray-600'>{data.candidate?.email} {data.candidate?.phone ? `• ${data.candidate?.phone}` : ''}</div>
              <div className='text-gray-600'>Experience: {data.candidate?.experience ?? '-'}</div>
              <div className='text-gray-600'>Company: {data.candidate?.currentCompany ?? '-'} • Role: {data.candidate?.currentRole ?? '-'}</div>
              {resumeHref && (
                <div className='mt-2'><a href={resumeHref} target='_blank' rel='noreferrer' className='btn btn-sm btn-light-primary'>View Resume</a></div>
              )}
            </div>
          </div>
          <div className='col-lg-6'>
            <div className='mb-6'>
              <div className='fw-bold fs-4 mb-2'>Job</div>
              <div className='text-gray-700'>{data.jobPost?.title}</div>
              <div className='text-gray-600'>{data.jobPost?.department} {data.jobPost?.location ? `• ${data.jobPost?.location}` : ''}</div>
            </div>
          </div>
        </div>

        <div className='separator my-6'></div>

        {/* Scores section */}
        <div className='row g-6 mb-6'>
          <div className='col-md-3'>
            <div className='bg-light-primary rounded p-5 h-100'>
              <div className='text-muted fs-7'>Overall Score</div>
              <div className='fs-2 fw-bold'>{data.overallScore ?? '-'}</div>
            </div>
          </div>
          <div className='col-md-3'>
            <div className='bg-light rounded p-5 h-100'>
              <div className='text-muted fs-7'>Resume Score</div>
              <div className='fs-3 fw-bold'>{data.resumeScore ?? '-'}</div>
            </div>
          </div>
          <div className='col-md-3'>
            <div className='bg-light rounded p-5 h-100'>
              <div className='text-muted fs-7'>Skill Match</div>
              <div className='fs-3 fw-bold'>{data.skillMatchScore ?? '-'}</div>
            </div>
          </div>
          <div className='col-md-3'>
            <div className='bg-light rounded p-5 h-100'>
              <div className='text-muted fs-7'>Experience Score</div>
              <div className='fs-3 fw-bold'>{data.experienceScore ?? '-'}</div>
            </div>
          </div>
        </div>

        <div className='row g-5'>
          <div className='col-md-6'>
            <label className='form-label'>Stage / Status</label>
            <select className='form-select' value={status} onChange={(e)=>setStatus(e.target.value)}>
              {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className='col-md-6'>
            <label className='form-label'>Internal Notes</label>
            <input className='form-control' value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder='Optional notes' />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ApplicationDetail


